import React from 'react';

interface AnimatedGradientBackgroundProps {
  /**
   * Array of color stops for the gradient
   * Each stop should be an object with color and position
   * Example: [{ color: '#F8FFE5', position: '0%' }, { color: '#E9F7FE', position: '100%' }]
   */
  colors: Array<{
    color: string;
    position: string;
  }>;
  /**
   * Animation duration in seconds for each color cycle
   * Default: 60s
   */
  duration?: number;
  /**
   * Gradient rotation angle in degrees
   * Default: 110
   */
  rotation?: number;
  /**
   * Optional className for additional styling
   */
  className?: string;
}

export const AnimatedGradientBackground: React.FC<AnimatedGradientBackgroundProps> = ({
  colors,
  duration = 60,
  rotation = 110,
  className = '',
}) => {
  // Generate unique ID for this gradient instance
  const gradientId = React.useId();
  
  // Create animation values by cycling through colors
  const generateAnimationValues = (colorIndex: number) => {
    const values: string[] = [];
    const step = 100 / colors.length;
    
    for (let i = 0; i <= colors.length; i++) {
      const currentIndex = (colorIndex + i) % colors.length;
      values.push(colors[currentIndex].color);
    }
    
    return values.join(';');
  };

  return (
    <div className={`fixed inset-0 -z-10 ${className}`}>
      <svg 
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
      >
        <defs>
          <linearGradient
            id={gradientId}
            gradientTransform={`rotate(${rotation})`}
            x="-0%"
            y="0%"
          >
            {colors.map((colorStop, index) => (
              <stop
                key={index}
                offset={colorStop.position}
                stopColor={colorStop.color}
              >
                <animate
                  attributeName="stop-color"
                  values={generateAnimationValues(index)}
                  dur={`${duration}s`}
                  repeatCount="indefinite"
                />
              </stop>
            ))}
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${gradientId})`} />
      </svg>
    </div>
  );
};

// Preset configurations for Luminar Consult pages
export const GradientPresets = {
  // Homepage - "Turning potential into intelligent impact"
  // Fresh, innovative, professional - Vibrant yellow-green to cool blue (kept original)
  homepage: {
    colors: [
      { color: '#B3E5FC', position: '0%' },    // Sky blue
      { color: '#81D4FA', position: '33%' },   // Medium blue
      { color: '#A5D6A7', position: '66%' },   // Light green
      { color: '#C8E6C9', position: '100%' }   // Pale green
    ],
    rotation: 110,
    duration: 60
  },
  
  // About - "Why Luminar"
  // Trustworthy, warm - Soft amber to cool aqua
  about: {
    colors: [
      { color: '#FFF9E6', position: '0%' },    // Warm cream
      { color: '#FFECB3', position: '33%' },   // Light amber
      { color: '#B3E5FC', position: '66%' },   // Light cyan
      { color: '#E0F7FA', position: '100%' }   // Pale aqua
    ],
    rotation: 135,
    duration: 55
  },
  
  // Specialisms - "What We Do"
  // Technical, innovative - Lavender to mint green
  specialisms: {
    colors: [
      { color: '#E8EAF6', position: '0%' },    // Soft lavender
      { color: '#C5CAE9', position: '33%' },   // Light indigo
      { color: '#B2DFDB', position: '66%' },   // Teal
      { color: '#C8E6C9', position: '100%' }   // Light green
    ],
    rotation: 90,
    duration: 50
  },
  
  // Sectors - "Where We Work"
  // Diverse, cross-sector - Sky blue through lime to aqua
  sectors: {
    colors: [
      { color: '#E1F5FE', position: '0%' },    // Light blue
      { color: '#B3E5FC', position: '25%' },   // Sky blue
      { color: '#DCEDC8', position: '50%' },   // Lime green
      { color: '#B2EBF2', position: '75%' },   // Cyan
      { color: '#E0F2F1', position: '100%' }   // Pale teal
    ],
    rotation: 100,
    duration: 65
  },
  
  // Impact - "Results That Matter"
  // Evidence-based, credible - Ocean blues with growth greens
  impact: {
    colors: [
      { color: '#F8FFE5', position: '0%' },    // Soft yellow-green
      { color: '#DEF4F2', position: '33%' },   // Light teal
      { color: '#C1E9FF', position: '66%' },   // Sky blue
      { color: '#E9F7FE', position: '100%' }   // Pale blue
    ],
    rotation: 120,
    duration: 58
  },
  
  // Contact - "Let's Work Together"
  // Approachable, inviting - Warm yellow to cool blue
  contact: {
    colors: [
      { color: '#FFF9C4', position: '0%' },    // Light yellow
      { color: '#FFECB3', position: '33%' },   // Pale amber
      { color: '#B3E5FC', position: '66%' },   // Sky blue
      { color: '#E1F5FE', position: '100%' }   // Pale blue
    ],
    rotation: 125,
    duration: 52
  }
};